import { useState } from 'react';
import { useQueryClient } from 'react-query';
import useHttpMutation from '@actions/services/firestore/browser/useHttpMutation';
import type {
    UseCreateFolderParams,
    UseCreateFolderResult,
    CreateFolderInput,
    CreateFolderResponse,
    Folder,
} from './types';

interface CreateFolderRequestBody {
    path: string;
    name: string;
    agentId: string;
    entityType: string;
}

const useCreateFolder = ({ agentId, entityType }: UseCreateFolderParams): UseCreateFolderResult => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<Error | undefined>();

    const { mutateAsync, isLoading: isCreating } = useHttpMutation<
        CreateFolderRequestBody,
        CreateFolderResponse
    >({
        pathname: '/folders',
        method: 'POST',
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [`folders-${agentId}-${entityType}`] });
        },
        onError: (err: Error) => {
            setError(err);
        },
    });

    const createFolder = async (input: CreateFolderInput): Promise<Folder> => {
        setError(undefined);
        const response = await mutateAsync({
            body: {
                path: input.path,
                name: input.name,
                agentId,
                entityType,
            },
        });
        return response.data;
    };

    return {
        createFolder,
        isCreating,
        error,
    };
};

export default useCreateFolder;
